import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {size, cloneDeep, isEmpty} from 'lodash';

import {getPercent} from '@ey/utils';

import {labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';
import {milestonesFilterId, projectSessionKeys} from '../../../../../../util/uiconstants';

import {keyEventsCustomizeIds} from '../../../../../Common/MetricsTable/columns';

import BadgeLabel from '../../../../../Common/Badge/BadgeLabel';
import ExportToExcel from '../../../../../Common/ExportToExcel/ExportToExcel';
import FilterChips from '../../../../../Common/FilterChips';
import {loadStorage, saveStorage} from '../../../../../Common/localStorage';

import Select from '../../../../../CoreWrappers/Select';
import MetricsTable from '../../../../../Common/MetricsTable';

const MilestonesEngagementTable = (props) => {
	const [numberFormat, userDateFormat] = useSelector(({userPreferences}) => [
		userPreferences.numberFormat,
		userPreferences.dateFormat
	]);
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);
	const dashboardMilestonesFilters = useSelector(({dashboardFilters}) => dashboardFilters.milestones);
	const maxCustomColumns = 4;
	const hasMilestoneFiltersApplyed = dashboardMilestonesFilters != null;

	const filters = [
		{id: milestonesFilterId.milestones, label: labels.milestones},
		{id: milestonesFilterId.keyEvents, label: labels.keyEvents}
	];

	const keyEventsCustomizeOptions = [
		{id: keyEventsCustomizeIds.reportReleaseDateId, label: labels.reportReleaseDate},
		{id: keyEventsCustomizeIds.partnerInvitedId, label: labels.partnerInvited},
		{id: keyEventsCustomizeIds.picOnCraId, label: labels.picSignoffOnCra},
		{id: keyEventsCustomizeIds.picOnAuditPlanId, label: labels.picSignoffOnAuditPlan},
		{id: keyEventsCustomizeIds.eqrOnCraId, label: labels.eqrSignoffOnCra}
	];

	const keyEventsCustomizeInitial = [
		keyEventsCustomizeIds.partnerInvitedId,
		keyEventsCustomizeIds.picOnCraId,
		keyEventsCustomizeIds.picOnAuditPlanId,
		keyEventsCustomizeIds.eqrOnCraId
	];

	const [milestoneValue, setMilestoneValue] = useState(null);
	const [selectedFilter, setSelectedFilter] = useState(milestonesFilterId.milestones);
	const [engagementData, setEngagementData] = useState([]);
	const [showMessage, setShowMessage] = useState(false);
	const [userChange, setUserChange] = useState(false);
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.milestones}`;
	const milestonesOptions = Utility.getMilestoneOptionWithGroup();

	const isKeyEvents = selectedFilter === milestonesFilterId.keyEvents;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		if (currentState && currentState.milestoneFilterid) {
			setSelectedFilter(currentState.milestoneFilterid);
		}
	}, []);

	useEffect(() => {
		setDefaultMilestoneValue();
	}, [milestonesOptions]);

	useEffect(() => {
		if (!userChange) {
			const currentState = loadStorage(psessionKEy);
			if (currentState && !currentState.milestoneValueFilterid) {
				setMilestoneValue(milestonesOptions[0].options[0]);
			}
		}
	}, [engagementData]);

	useEffect(() => {
		if (milestoneValue?.id) {
			buildTable();
		}
	}, [props.engagementMetrics, milestoneValue, selectedFilter]);

	useEffect(() => {
		if (dashboardMilestonesFilters) {
			setFilterChips();
		} else {
			resetFilterChips();
		}
		setUserChange(true);
	}, [dashboardMilestonesFilters]);

	const setFilterChips = () => {
		//Set filter.
		const selectedFilterId = dashboardMilestonesFilters.filters[0].type;
		if (selectedFilterId === milestonesFilterId.milestones) {
			let selectedOption = null;
			milestonesOptions.every(({options}) => {
				selectedOption = options.filter(({label}) => label === dashboardMilestonesFilters.filters[0].categoryLabel);
				if (selectedOption.length > 0) {
					selectedOption = selectedOption[0];
					return false;
				}
				return true;
			});
			setMilestoneValue((prevState) => {
				if (prevState.id === selectedOption?.id) {
					buildTable();
				}
				return selectedOption;
			});
		}
		setSelectedFilter((prevState) => {
			if (prevState === selectedFilterId) {
				buildTable();
			}
			return selectedFilterId;
		});
	};

	const resetFilterChips = () => {
		//Set all to default.
		setSelectedFilter(milestonesFilterId.milestones);
		setMilestoneValue((prevState) => {
			if (prevState.id === milestonesOptions[0].options[0].id) {
				buildTable();
			}
			return milestonesOptions[0].options[0];
		});
	};

	const getExportDetails = () => {
		return {
			title: filters.find((f) => f.id === selectedFilter).label,
			columns: getColumnsToExport(),
			data: getDataToExport(),
			worksheetName: filters.find((f) => f.id === selectedFilter).label
		};
	};

	const getColumnsToExport = () => {
		let columns = [];
		columns.push({header: labels.orgChartEntity, key: 'entityName'});
		columns.push({header: labels.engagementId, key: 'engId'});
		columns.push({header: labels.contactName, key: 'ContactName'});
		columns.push({header: labels.contactEmail, key: 'ContactEmail'});
		columns.push({header: labels.engagementLabel, key: 'engDes'});
		if (isKeyEvents) {
			columns.push({header: labels.reportReleaseDate, key: 'reportReleaseDate'});
			columns.push({header: labels.partnerInvited, key: 'partnerInvited'});
			columns.push({header: labels.picSignoffOnCra, key: 'picSignoffOnCra'});
			columns.push({header: labels.eqrSignoffOnCra, key: 'eqrSignoffOnCra'});
			columns.push({header: labels.picSignoffOnAuditPlan, key: 'picSignoffOnAuditPlan'});
		} else {
			columns.push({header: labels.start, key: 'start'});
			columns.push({header: labels.end, key: 'end'});
			columns.push({header: labels.statusComplete, key: 'statusComplete'});
			columns.push({header: labels.statusInReview, key: 'statusInReview'});
			columns.push({header: labels.statusInProgress, key: 'statusInProgress'});
			columns.push({header: labels.statusOpen, key: 'statusOpen'});
		}
		return columns;
	};

	const getDataToExport = () => {
		if (isKeyEvents) {
			return createKeyEventsDataToExport();
		} else {
			return createMilestonesDataToExport();
		}
	};

	const createMilestonesDataToExport = () => {
		let data = [];
		let selectedMilestone = milestoneValue ?? milestonesOptions[0].options[0];
		dashboardContext.orgChart.forEach(() => {
			if (dashboardContext && size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = props.engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								let ms = cloneDeep(
									engMetricsData.mS?.find((m) => {
										return m.tPTId === selectedMilestone.id;
									})
								);
								let totalMilestones = ms ? ms?.tC + ms?.tIR + ms?.tIP + ms?.tO : 0;
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									start: Utility.isValidDate(ms?.sD) ? Utility.getFormattedDate(ms?.sD, userDateFormat) : '',
									end: Utility.isValidDate(ms?.eD) ? Utility.getFormattedDate(ms?.eD, userDateFormat) : '',
									statusComplete: getNumberWithPercentage(ms?.tC, totalMilestones),
									statusInReview: getNumberWithPercentage(ms?.tIR, totalMilestones),
									statusInProgress: getNumberWithPercentage(ms?.tIP, totalMilestones),
									statusOpen: getNumberWithPercentage(ms?.tO, totalMilestones)
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const createKeyEventsDataToExport = () => {
		let data = [];
		dashboardContext.orgChart.forEach(() => {
			if (dashboardContext && size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = props.engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									reportReleaseDate: Utility.isValidDate(engMetricsData.relDate)
										? Utility.getFormattedDate(engMetricsData.relDate, userDateFormat)
										: '',
									partnerInvited: engMetricsData.partInv ? labels.yes : labels.no,
									picSignoffOnCra: engMetricsData.hasCRAPICSO ? labels.yes : labels.no,
									eqrSignoffOnCra: engMetricsData.hasCRAEQRSO ? labels.yes : labels.no,
									picSignoffOnAuditPlan: engMetricsData.hasAPPICSO ? labels.yes : labels.no
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const getNumberWithPercentage = (partialValue, total) => {
		return (
			total &&
			partialValue &&
			`${partialValue} (${Utility.getFormattedNumber(numberFormat, getPercent(partialValue, total))}%)`
		);
	};

	const setDefaultMilestoneValue = () => {
		if (milestonesOptions && milestoneValue === null) {
			let option = milestonesOptions[0].options[0];
			const currentState = loadStorage(psessionKEy);
			if (currentState && currentState.milestoneValueFilterid) {
				setMilestoneValue(currentState.milestoneValueFilterid);
			} else {
				setMilestoneValue(option);
			}
		}
	};

	const buildTable = () => {
		// check if selected entities have engagements
		if (dashboardContext.entities?.every((entity) => entity.engagements?.length === 0)) {
			setShowMessage(true);
		} else {
			let data = [];

			// show entities base on org chart order and if it was selected
			dashboardContext.orgChart.forEach((node) => {
				loadNodes(node, dashboardContext.entities, data);
			});

			setShowMessage(false);

			// when searching, remove entity without engagement
			if (hasMilestoneFiltersApplyed) {
				data = data?.filter((x) => x?.items?.length !== 0);
			}

			setEngagementData(data);
		}
	};

	const loadNodes = (node, entities, data) => {
		// check if entity is selected
		let entity = entities.find((x) => x.id === node.id);

		if (entity && entity.engagements?.length > 0) {
			const items = getEngagements(entity);
			let group = {
				id: entity.id,
				name: entity.label,
				items,
				customGroupRender: () => {
					let result = <BadgeLabel label={entity.label} value={items.length} />;

					return result;
				}
			};

			group.engCount = group.items?.length;

			if (entity.isEnabled) {
				data.push(group);
			}
		}

		// load children entities for each node
		node.nodes.forEach((n) => {
			loadNodes(n, entities, data);
		});
	};

	const getEngagements = (entity) => {
		let engagements = entity.engagements.map((engagementId) => {
			// key events & milestone use same engagement object
			return getEngagement(engagementId);
		});

		let areEngagements = engagements.filter((e) => e !== undefined);

		if (hasMilestoneFiltersApplyed) {
			areEngagements = areEngagements.filter(({engId}) => dashboardMilestonesFilters?.engagements?.includes(engId));
		}

		if (isEmpty(areEngagements)) {
			return [];
		} else {
			return areEngagements;
		}
	};

	const getEngagement = (id) => {
		const item = {...props.engagementMetrics?.find((x) => x.engId === id)};

		if (selectedFilter === milestonesFilterId.milestones && milestoneValue?.id && item) {
			let ms =
				cloneDeep(
					item.mS?.find((m) => {
						return m.tPTId === milestoneValue.id;
					})

					// if no milestones data was found we create the object empty to include all the engagements
				) || {};

			ms.engDes = item.engDes;
			ms.engId = item.engId;
			ms.engStatus = item.engStatus;
			ms.milestoneId = milestoneValue?.id;

			return ms;
		}

		return item;
	};

	const showMilestoneDropdown = () => {
		return selectedFilter === milestonesFilterId.milestones;
	};

	const cleanDashboardFilters = () => {
		dashboardMilestonesFilters?.onClearFilter && dashboardMilestonesFilters?.onClearFilter();
	};

	const handlerOnSwitchChange = (id) => {
		let _id = Number.parseInt(id);

		setSelectedFilter(Number.parseInt(_id));
		setUserChange(true);
		buildTable();

		let currentState = loadStorage(psessionKEy);
		if (!currentState) {
			currentState = {};
		}
		currentState.milestoneFilterid = _id;
		saveStorage(psessionKEy, currentState);
		cleanDashboardFilters();
	};

	const handlerOnMilestoneChange = (option) => {
		setMilestoneValue(option);
		setUserChange(true);
		let currentState = loadStorage(psessionKEy);
		if (!currentState) {
			currentState = {};
		}
		currentState.milestoneValueFilterid = option;
		saveStorage(psessionKEy, currentState);
	};

	const renderMilestoneSelect = () => {
		const id = 'milestones-single';

		const options = [];
		milestonesOptions.forEach((group) => {
			const id = `group_${group.id}`;
			options.push({id, value: id, label: group.label, disabled: true}, ...group.options);
		});

		const onChange = (id) => {
			const options = [];
			milestonesOptions.forEach((x) => {
				options.push(...x.options);
			});
			handlerOnMilestoneChange(options.find((y) => y.id === id));
		};

		const selectProps = {
			id,
			value: milestoneValue?.id,
			onChange,
			label: labels.milestones,
			options
		};

		return showMilestoneDropdown() && <Select {...selectProps} />;
	};

	const renderTableSelect = () => {
		const selectProps = {
			value: selectedFilter,
			wrapperClass: 'table-select',
			options: filters,
			onChange: handlerOnSwitchChange
		};

		return <Select {...selectProps} />;
	};

	const renderCustomDetails = () => {
		if (dashboardMilestonesFilters?.filters) {
			const filterChipsProps = {
				filters: [...dashboardMilestonesFilters?.filters],
				onClearFilter: dashboardMilestonesFilters?.onClearFilter
			};

			return <FilterChips {...filterChipsProps} />;
		}
	};

	const tableProps = {
		data: engagementData,
		variant: selectedFilter === milestonesFilterId.milestones ? 'milestones' : 'keyevents',
		renderHeader: (renderCustomizeColumns) => (
			<>
				{renderMilestoneSelect()}
				{isKeyEvents && renderCustomizeColumns(labels.selectUpToFourOptionHeader)}
				{renderTableSelect()}
				<ExportToExcel getDataToExport={getExportDetails} className="export-data" />
			</>
		),
		renderFilter: renderCustomDetails
	};

	if (isKeyEvents) {
		tableProps.columnVisibilityOptions = keyEventsCustomizeOptions;
		tableProps.columnVisibility = keyEventsCustomizeInitial;
		tableProps.maxCustomColumns = maxCustomColumns;
	}

	return <MetricsTable {...tableProps} />;
};

MilestonesEngagementTable.defaultProps = {
	engagementMetrics: []
};

MilestonesEngagementTable.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default MilestonesEngagementTable;
